import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  useAddProjectVirtualResource,
  useRolesRetrieval,
} from "@planzone/view-models";
import {
  Resource,
  resource,
  ResourceTypes,
  role as roleMock,
} from "@planzone/dto";
import { LayoutWatcher } from "@common/layoutWatcher";
import { ScreenWatcher } from "@common/screenWatcher";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { Props } from "./types";
import { VirtualResourceForm } from "./virtualResourceForm";
import styles from "./styles.scss";

function ProjectVirtualResourceAddition({ project, cancel }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, addProjectVirtualResource } =
    useAddProjectVirtualResource();
  const { roles } = useRolesRetrieval();

  const [formData, setFormData] = React.useState<Resource[]>([
    { ...resource, type: ResourceTypes.VIRTUAL },
  ]);
  const role = roles.find(({ name }) => name === "pr_worker") || {
    ...roleMock,
    uuid: "",
  };

  return (
    <Modal header={t("projectVirtualResourceAddition.title")} close={cancel}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          formData.forEach((resource) =>
            addProjectVirtualResource({
              role: resource.role || role,
              project,
              resource,
            }),
          );
        }}
        className={styles.main}
      >
        <div className={styles.row}>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("virtualResourceForm.nameTitle")}
            </h3>
          </div>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("virtualResourceForm.roleTitle")}
            </h3>
          </div>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("virtualResourceForm.professionTitle")}
            </h3>
          </div>
        </div>
        <ScreenWatcher
          renderChildren={({ height }) => (
            <LayoutWatcher
              renderChildren={({ top }) => {
                const maxHeight = height - top - 60;
                const [reachedMaxHeight, setReachedMaxHeight] = useState(false);
                const elementRef = useRef<HTMLDivElement>(null);

                const hasReachedMaxHeight = () => {
                  const element = elementRef.current;
                  if (element) {
                    const hasReachedMaxHeight =
                      element.scrollHeight >= maxHeight - 60;
                    setReachedMaxHeight(hasReachedMaxHeight);
                  }
                };

                return (
                  <div
                    ref={elementRef}
                    className={classNames(styles.forms, {
                      [styles.formsOverflow]: reachedMaxHeight,
                    })}
                    style={{ maxHeight }}
                  >
                    {formData.map((data, index) => (
                      <div
                        key={index}
                        style={{ zIndex: formData.length - index }}
                      >
                        <VirtualResourceForm
                          formData={data}
                          edit={(nextData) =>
                            setFormData([
                              ...formData.slice(0, index),
                              nextData,
                              ...formData.slice(index + 1),
                            ])
                          }
                        />
                      </div>
                    ))}
                    <p
                      className={styles.addResourceButton}
                      onClick={() => {
                        hasReachedMaxHeight();
                        setFormData([...formData, resource]);
                      }}
                    >
                      + {t("projectVirtualResourceAddition.addResource")}
                    </p>
                  </div>
                );
              }}
            />
          )}
        />
        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("projectVirtualResourceAddition.cancel")}
          </Button>
          <Button type="submit" status={isRequestPending ? "pending" : "none"}>
            {t("projectVirtualResourceAddition.confirm")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { ProjectVirtualResourceAddition };
